<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

// [VITE] [TODO] We are using vue-virtual-scroll for now, since that seemed the simplest way to migrate
// from the current one. But we should consider using tanstack virtual in the future
// https://tanstack.com/virtual/latest/docs/framework/vue/examples/variable
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import SzAssigneeChips from './widgets/conversation/solarz/SzAssigneeChips.vue';
import SzActiveFilterBanner from 'dashboard/components-next/sidebar/SzActiveFilterBanner.vue';
import SzChatListEmpty from './widgets/conversation/solarz/SzChatListEmpty.vue';
import SzFilterSheet from './widgets/conversation/solarz/SzFilterSheet.vue';
import ConversationItem from './ConversationItem.vue';
import SzConversationCardSkeleton from './widgets/conversation/solarz/SzConversationCardSkeleton.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import IntersectionObserver from './IntersectionObserver.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useEventListener } from '@vueuse/core';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const conversationListRef = ref(null);
const conversationDynamicScroller = ref(null);

provide('contextMenuElementTarget', conversationDynamicScroller);

// SZ: helpers de sessionStorage scope-por-conta — chave separada por accountId
// pra evitar vazar filtros entre accounts no mesmo browser/tab.
function szAccountId() {
  // /app/accounts/N/... — extrai N
  const m = (window.location.pathname || '').match(/\/accounts\/(\d+)/);
  return m ? m[1] : 'default';
}
function szChipStorageKey() {
  return `sz_chip_filter_order_acc_${szAccountId()}`;
}
function szAppliedFiltersStorageKey() {
  return `sz_applied_filters_acc_${szAccountId()}`;
}

// SZ: chips cumulativos com ORDEM. Array<'me'|'unread'|'mentions'>.
// [0] = primary (define scope no backend). Demais = overlays client-side.
// Default ['me'].
// Persistido em sessionStorage pra sobreviver remounts E reloads (F5).
function loadChipOrder() {
  try {
    const raw = sessionStorage.getItem(szChipStorageKey());
    if (!raw) return ['me'];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return ['me'];
    const VALID = new Set(['me', 'unread', 'mentions']);
    const cleaned = parsed.filter(k => VALID.has(k));
    return cleaned.length ? cleaned : ['me'];
  } catch {
    return ['me'];
  }
}
function loadStoredAppliedFilters() {
  try {
    const raw = sessionStorage.getItem(szAppliedFiltersStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}
const chipFilterOrder = ref(loadChipOrder());
const primaryChip = computed(() => chipFilterOrder.value[0] || null);

// activeAssigneeTab vira computed (compat com código existente que lê 'me'/'all'/'unassigned').
// Só vira 'me' se 'me' for PRIMARY; senão 'all' (e 'me' vira overlay client-side).
const activeAssigneeTab = computed(() =>
  primaryChip.value === 'me' ? 'me' : 'all'
);

// SZ: guarda contra loop infinito do IntersectionObserver quando overlay client-side
// derruba todos os itens da página recém-fetcheada. Após N páginas "secas",
// suspende paginação automática até o filtro mudar.
const OVERLAY_DRY_PAGE_LIMIT = 3;
const overlayDryPages = ref(0);
const lastVisibleCount = ref(0);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
// SZ: default sort agora é last_activity_at_desc — backend (SORT_OPTIONS no
// conversation_finder.rb) não conhece last_message_at_* e cai no fallback.
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

// computed
const intersectionObserverOptions = computed(() => {
  return {
    root: conversationListRef.value,
    rootMargin: '100px 0px 100px 0px',
  };
});

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

// SZ: decide qual variante do empty state mostrar com base no contexto.
// Prioridade: filtros aplicados > 2+ chips > mentions > unread > mine (default).
// 2+ chips combinados caem no generico (filtered) pq nao tem mensagem
// especifica que represente bem combinacoes.
const emptyStateFilter = computed(() => {
  if (hasAppliedFiltersOrActiveFolders.value) return 'filtered';
  const chips = szActiveFilters.value || [];
  if (chips.length > 1) return 'filtered';
  if (chips.includes('mentions')) return 'mentions';
  if (chips.includes('unread')) return 'unread';
  return 'mine';
});

// SZ: handler do CTA "Mostrar minhas conversas" — desliga chips ativos
// (deixa só mine) emitindo toggle-filter conforme estado atual.
function handleShowMine() {
  const chips = Array.from(szActiveFilters.value || []);
  // Desliga unread/mentions se ligados
  for (const k of ['unread', 'mentions']) {
    if (chips.includes(k)) toggleChipFilter(k);
  }
  // Liga mine se não tiver
  if (!chips.includes('mine')) toggleChipFilter('mine');
}

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[countKey] || 0,
  }));
});

const showAssigneeInConversationCard = computed(() => {
  // SZ: sempre mostrar assignee. A lógica antiga escondia quando tab='me'
  // assumindo que todas seriam do user, mas com chips cumulativos (Brasa),
  // o chip 'me' inclui participantes — o assignee real pode ser outro.
  return true;
});

// SZ: 'unread' é filtro client-side em cima da lista 'all'; mapeia pra 'all'
// nos lookups de paginação para reusar o estado do tab base.
const pageScopeKey = computed(() =>
  activeAssigneeTab.value === 'unread' ? 'all' : activeAssigneeTab.value
);

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : pageScopeKey.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  pageScopeKey
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

// SZ: contagens estáveis vindas do backend (endpoint /conversations/sz_chip_counts).
// Substitui o cálculo client-side que variava com scroll.
const szBackendCounts = ref({ me: 0, unread: 0, mentions: 0 });

async function fetchSzChipCounts() {
  try {
    const accountId = currentAccountId.value;
    if (!accountId) return;
    const status = activeStatus.value || 'open';
    const inboxId = props.conversationInbox ? String(props.conversationInbox) : null;
    const baseUrl = `/api/v1/accounts/${accountId}/conversations/sz_chip_counts`;

    // SZ: quando há filtros customizados aplicados (SzFilterSheet) → POST com payload
    // (FilterService no backend). Senão, GET com params URL-driven (label/team/etc.)
    // — esses NÃO passam pelo Vuex appliedFilters. Sem isso o badge "Não Lidas"
    // mostra contagem global mesmo com lista filtrada por etiqueta vazia.
    let resp;
    if (hasAppliedFilters.value) {
      const body = {
        payload: appliedFilters.value,
        status,
      };
      if (inboxId) body.inbox_id = inboxId;
      // eslint-disable-next-line no-undef
      resp = await axios.post(baseUrl, body);
    } else {
      const params = new URLSearchParams();
      if (inboxId) params.set('inbox_id', inboxId);
      params.set('status', status);
      // URL-driven filters (sidebar nav)
      if (props.label) params.append('labels[]', props.label);
      if (props.teamId) params.set('team_id', String(props.teamId));
      if (props.conversationType) params.set('conversation_type', props.conversationType);
      // assignee_type vem do tab ativo (Minhas/Não Atribuídas/Atribuídas)
      const assigneeTab = activeAssigneeTab.value;
      if (['me', 'unassigned', 'assigned'].includes(assigneeTab)) {
        params.set('assignee_type', assigneeTab);
      }
      // eslint-disable-next-line no-undef
      resp = await axios.get(`${baseUrl}?${params.toString()}`);
    }

    const data = resp?.data || {};
    szBackendCounts.value = {
      me: Number(data.me) || 0,
      unread: Number(data.unread) || 0,
      mentions: Number(data.mentions) || 0,
    };
  } catch (e) {
    // Falha silenciosa — chips ficam com 0/sem badge.
  }
}

// Mantido como compat — não usado mais, sempre 0.
const szUnreadCount = computed(() => 0);

const activeAssigneeTabCount = computed(() => {
  const found = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  );
  return found?.count ?? 0;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const conversationFilters = computed(() => {
  // SZ: assigneeType derivado de primaryChip (já feito via activeAssigneeTab computed).
  // conversationType: 'mention' sempre que 'mentions' está no set (em qualquer posição).
  // Não há forma de detectar menção client-side, então mentions só funciona via backend
  // — independente da ordem, força conversation_type=mention quando presente.
  const wantsMentions = chipFilterOrder.value.includes('mentions');
  const wantsUnread = chipFilterOrder.value.includes('unread');
  const conversationType = wantsMentions
    ? 'mention'
    : (props.conversationType || undefined);
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType,
    // SZ: filtro 'unread' server-side via initializer sz_unread_filter_patch.
    // Antes era overlay client-side, mas paginação por last_activity_at deixava
    // unread antigos (>1ª página) inacessíveis na lista.
    unreadOnly: wantsUnread ? true : undefined,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

// SZ: banner "X conversas em [pill]" — aparece quando há filtro de etiqueta,
// canal ou time ativo (vindo da sidebar via props). Count vem do conversationStats
// (meta) — mesma fonte da contagem real da lista. Ver activeFilterCount abaixo.
const activeFilterBannerType = computed(() => {
  if (props.label) return 'label';
  if (props.conversationInbox) return 'inbox';
  if (props.teamId) return 'team';
  return null;
});

const activeFilterLabelObj = computed(() => {
  if (activeFilterBannerType.value !== 'label') return null;
  return labels.value.find(l => l.title === props.label) || { title: props.label, color: '#525252' };
});

const activeFilterInboxObj = computed(() => {
  if (activeFilterBannerType.value !== 'inbox') return null;
  return inboxesList.value.find(i => i.id === Number(props.conversationInbox))
    || { id: props.conversationInbox, name: '' };
});

const activeFilterTeamObj = computed(() => {
  if (activeFilterBannerType.value !== 'team') return null;
  return teamsList.value.find(t => t.id === Number(props.teamId))
    || { id: props.teamId, name: '' };
});

// SZ_BANNER_COUNT_FIX_20260608: o contador do banner agora reflete o filtro ATIVO
// da lista (status da aba Abertas/Fechadas/Todas + assignee + inbox/etiqueta/time),
// via conversationStats (meta do Chatwoot) — a MESMA fonte da contagem real da
// lista. Antes vinha do szSidebarCounts (endpoint sz_sidebar_counts), que contava
// só status=open e por isso divergia nas abas Fechadas/Todas.
window.__szBannerCountFix = '20260608';
const activeFilterCount = computed(() => {
  if (!activeFilterBannerType.value) return 0;
  const stats = conversationStats.value || {};
  return activeAssigneeTab.value === 'me'
    ? stats.mineCount || 0
    : stats.allCount || 0;
});

function onCloseFilterBanner() {
  // Volta pra "Todas as conversas" da conta
  router.push({ name: 'home' });
}

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  // SZ: overlays client-side dos chips — aplicados em AMBOS os modos.
  // - 'unread' agora é SERVER-SIDE via unread_only=true no fetch (initializer
  //   sz_unread_filter_patch). Sem overlay aqui — backend já entrega filtrado.
  // - 'me' overlay quando não é primary scope OU quando há filtros aplicados
  //   (no modo filtros aplicados, todos os chips agem como overlays).
  // - 'mentions' não tem overlay client-side (sem campo visível); em modo
  //   sem filtro vai via conversation_type=mention; em modo com filtro
  //   o chip pode estar marcado mas sem efeito (trade-off aceitável).
  const order = chipFilterOrder.value;
  const primary = primaryChip.value;
  const myId = currentUser.value?.id;
  const isFiltered = hasAppliedFiltersOrActiveFolders.value;
  if (order.includes('me') && (isFiltered || primary !== 'me') && myId) {
    localConversationList = localConversationList.filter(
      c => c.meta?.assignee?.id === myId
    );
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  // SZ: chip 'unread' — overlay client-side pra esconder conversas que
  // foram lidas no client (unread_count == 0) mas ainda estão na lista
  // server-side (fetch antigo). Sem isso, conversa fica visível no chip
  // 'Não Lidas' até próximo refetch (troca de filtro, scroll, etc.).
  if (order.includes('unread')) {
    localConversationList = localConversationList.filter(
      c => (c.unread_count || 0) > 0
    );
  }

  return localConversationList;
});


const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  // SZ: normaliza valores legados last_message_at_* → last_activity_at_*
  // (backend não suporta last_message_at_*).
  const LEGACY_MAP = {
    last_message_at_asc: 'last_activity_at_asc',
    last_message_at_desc: 'last_activity_at_desc',
  };
  const normalized = LEGACY_MAP[orderBy] || orderBy;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    normalized
  )
    ? normalized
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
  // [VITE] removing this since the library has changed
  // nextTick(() => {
  //   // Addressing a known issue in the virtual list library where dynamically added items
  //   // might not render correctly. This workaround involves a slight manual adjustment
  //   // to the scroll position, triggering the list to refresh its rendering.
  //   const virtualList = conversationListRef.value;
  //   const scrollToOffset = virtualList?.scrollToOffset;
  //   const currentOffset = virtualList?.getOffset() || 0;
  //   if (scrollToOffset) {
  //     scrollToOffset(currentOffset + 1);
  //   }
  // });
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
      // SZ: leva a ordenação atual pra lista filtrada respeitar o sort.
      sortBy: activeSortBy.value,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

// SZ: handlers da nova sheet de filtros (substitui o modal nativo)
function onSzFilterApply({ sortBy, filters }) {
  // ordenação (separado do sistema de appliedFilters)
  if (sortBy && sortBy !== activeSortBy.value) {
    activeSortBy.value = sortBy;
    store.dispatch('setChatSortFilter', sortBy);
  }
  // se nenhum filtro foi escolhido, apenas atualiza sort e refetcha
  if (!filters || filters.length === 0) {
    resetAndFetchData();
    showAdvancedFilters.value = false;
    return;
  }
  appliedFilter.value = filters;
  // SZ: dispatch setConversationFilters ANTES do fetch — espelha a ConversationFilter nativa.
  // Sem isso, getAppliedConversationFiltersV2 fica vazio, hasAppliedFilters=false,
  // e o conversationList renderiza o estado local (mineChatsList/allChatList) em vez
  // do chatLists filtrado que voltou da API. Filtros de label/prioridade/data não aparecem.
  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters)))
  );
  onApplyFilter(filters);
  showAdvancedFilters.value = false;
}
function onSzFilterClear() {
  appliedFilter.value = [];
  if (hasAppliedFilters.value) {
    store.dispatch('clearConversationFilters');
  }
  resetAndFetchData();
  showAdvancedFilters.value = false;
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  // SZ: anti-loop. Quando há overlay client-side ativo ('me' como não-primary),
  // o IntersectionObserver pode disparar continuamente porque a lista filtrada
  // não preenche o viewport. Após N páginas "secas" (sem ganho de itens
  // visíveis), suspende paginação automática até o filtro mudar.
  // 'unread' não está mais aqui — agora é server-side (sz_unread_filter_patch).
  const order = chipFilterOrder.value;
  const primary = primaryChip.value;
  const hasClientOverlay =
    order.includes('me') && primary !== 'me';
  if (hasClientOverlay && overlayDryPages.value >= OVERLAY_DRY_PAGE_LIMIT) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Add a method to handle scroll events
function handleScroll() {
  const scroller = conversationDynamicScroller.value;
  if (scroller && scroller.hasScrollbar) {
    const { scrollTop, scrollHeight, clientHeight } = scroller.$el;
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      loadMoreConversations();
    }
  }
}

// SZ: detecta se estamos na rota dedicada de menções (compat com nav externa)
const isOnMentionsRoute = computed(
  () => route.name === 'conversation_mentions' ||
        route.name === 'conversation_through_mentions' ||
        props.conversationType === 'mention'
);

// SZ: array ordenado para o componente de chips. O componente só usa pra membership check.
const szActiveFilters = computed(() => chipFilterOrder.value);

// SZ: contagens para badge dos 3 chips — todas do backend agora
// via /conversations/sz_chip_counts. Não variam com scroll.
const szChipCounts = computed(() => ({
  me: szBackendCounts.value.me,
  unread: szBackendCounts.value.unread,
  mentions: szBackendCounts.value.mentions,
}));

// SZ: toggle do chip — cumulativo + ORDER-AWARE.
// Insere no final do array quando adiciona (preserva insertion order).
// Primeiro chip do array = primary (define scope no backend).
function toggleChipFilter(key) {
  const arr = [...chipFilterOrder.value];
  const idx = arr.indexOf(key);
  if (idx >= 0) {
    arr.splice(idx, 1);
  } else {
    arr.push(key);
  }
  chipFilterOrder.value = arr;
  resetBulkActions();
  emitter.emit('clearSearchInput');
  // Se estamos numa rota /mentions e o usuário toggla qualquer chip,
  // navega de volta pra home (rota base) para o filtro de mention vir
  // do chip, não da rota.
  if (isOnMentionsRoute.value) {
    router.push({
      name: 'home',
      params: { accountId: currentAccountId.value },
    });
    return;
  }
  // SZ: em modo de filtros aplicados, chips são overlays client-side puros —
  // NÃO refetchar (resetAndFetchData limparia os filtros aplicados via
  // clearConversationFilters). O conversationList re-computa automaticamente
  // via reatividade do Vue ao mudar chipFilterOrder.
  if (hasAppliedFiltersOrActiveFolders.value) {
    return;
  }
  resetAndFetchData();
}

// Compat com chamadas internas antigas (não usado mais pelos chips).
function updateAssigneeTab(_selectedTab) {
  // no-op: legado, substituído por toggleChipFilter.
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }

  // SZ: persist Mais Filtros (priority/assignee/labels/etc) when only the
  // status tab changes. Default Chatwoot behaviour wipes filters on every
  // status switch via clearConversationFilters; here we re-apply them with
  // the new status condition merged in.
  //
  // IMPORTANT: getAppliedConversationFiltersV2 returns filters in camelCase
  // (`attributeKey`/`filterOperator`/`queryOperator`). Earlier this code
  // checked `attribute_key` and unshifted a fresh status filter every tab
  // click — the array grew unbounded (badge count went 1→2→3→…). We
  // dedupe by removing all camelCase `attributeKey === 'status'` entries
  // and prepending exactly one with the new value.
  var hasFilters = appliedFilters.value && appliedFilters.value.length > 0;
  if (type === 'status' && hasFilters) {
    var nextStatus = value;
    var existingFilters = JSON.parse(JSON.stringify(appliedFilters.value));
    var nonStatusFilters = existingFilters.filter(function (f) {
      return f.attributeKey !== 'status' && f.attribute_key !== 'status';
    });
    var statusFilterCamel = {
      attributeKey: 'status',
      filterOperator: 'equal_to',
      values: [nextStatus],
      queryOperator: 'and',
    };
    var updatedFiltersCamel = [statusFilterCamel].concat(nonStatusFilters);
    resetBulkActions();
    store.dispatch('conversationPage/reset');
    store.dispatch('emptyAllConversations');
    // setConversationFilters expects snake_case; getter re-exposes camelCase.
    store.dispatch(
      'setConversationFilters',
      useSnakeCase(JSON.parse(JSON.stringify(updatedFiltersCamel)))
    );
    appliedFilter.value = updatedFiltersCamel;
    fetchFilteredConversations(updatedFiltersCamel);
    fetchSzChipCounts();
    return;
  }

  // SZ: ordenação mudou COM filtros aplicados → preserva os filtros e re-busca
  // a lista FILTRADA com o novo sort (sort_by vai na request e o FilterService o
  // respeita via sz_filter_service_sort_patch). Antes caía em resetAndFetchData →
  // clearConversationFilters → o filtro (ex.: período) sumia, sobrava só a ordem.
  if (type === 'sort' && hasFilters) {
    resetBulkActions();
    store.dispatch('conversationPage/reset');
    store.dispatch('emptyAllConversations');
    fetchFilteredConversations(appliedFilters.value);
    return;
  }

  resetAndFetchData();
  // SZ: status mudou → refetcha contagens (contagens são por status)
  if (type === 'status') {
    fetchSzChipCounts();
  }
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(conversationId, status, snoozedUntil) {
  store
    .dispatch('toggleStatus', {
      conversationId,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    });
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return store.getters.getConversationById(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
  // SZ: também refetcha as contagens dos chips
  fetchSzChipCounts();
});

// SZ_BANNER_COUNT_FIX_20260608: mantém o conversationStats sincronizado com o
// filtro ativo (inbox/etiqueta/time + status da aba) sempre que ele muda — assim
// o contador do banner bate com a lista. Sem isso, o stats só era refetchado em
// eventos realtime, deixando o banner stale ao trocar de aba. Debounced no módulo.
watch(
  conversationFilters,
  () => {
    if (!activeFilterBannerType.value) return;
    store.dispatch('conversationStats/get', conversationFilters.value);
  },
  { deep: true, immediate: true }
);

useEventListener(conversationDynamicScroller, 'scroll', handleScroll);

onMounted(() => {
  // SZ: legado de query.tab — remove se vier do uso antigo (não usado mais com chips cumulativos)
  if (route.query?.tab) {
    router
      .replace({ name: route.name, params: route.params, query: {} })
      .catch(() => {});
  }
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  // SZ: na montagem (inclui re-mount após navegação via histórico OU F5 fresh boot),
  // tenta restaurar filtros aplicados do sessionStorage se Vuex estiver vazio.
  // Faz isso apenas em rotas sem folder/foldersId (folder tem sua própria query).
  if (!hasAppliedFilters.value && !hasActiveFolders.value && !props.foldersId) {
    const stored = loadStoredAppliedFilters();
    if (stored && stored.length > 0) {
      store.dispatch('setConversationFilters', stored);
    }
  }
  // Após restauração: preserva filtros aplicados (Vuex) em vez de limpá-los.
  // resetAndFetchData() dispara clearConversationFilters e zera tudo — só usar
  // pra reset explícito (toggle chip, X clear, etc.).
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  if (hasActiveFolders.value) {
    appliedFilter.value = [];
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
    store.dispatch('campaigns/get');
  } else if (hasAppliedFilters.value) {
    // Hidrata o ref local a partir do Vuex (sobreviveram remount)
    appliedFilter.value = [...appliedFilters.value];
    fetchFilteredConversations(appliedFilters.value);
  } else if (!props.foldersId) {
    fetchConversations();
  }
  // SZ: busca inicial das contagens dos chips do backend
  fetchSzChipCounts();
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('updateConversationStatus', toggleConversationStatus);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => {
    resetAndFetchData();
    fetchSzChipCounts();
  }
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

// SZ: reset do contador de páginas secas quando o filtro de chip muda
// + persiste em sessionStorage (scope-por-conta) pra sobreviver remount/F5.
// + refetch quando 'unread' alterna (filtro server-side via unread_only).
watch(chipFilterOrder, (val, oldVal) => {
  overlayDryPages.value = 0;
  lastVisibleCount.value = 0;
  try {
    sessionStorage.setItem(szChipStorageKey(), JSON.stringify(val));
  } catch { /* sessionStorage indisponível — segue funcionando sem persistência */ }

  // Refetch quando 'unread' alterna (server-side filter via unread_only).
  // OU quando 'me' vira/deixa de ser primary (assignee_type=me no backend).
  // Sem isso o Vuex mantém a lista antiga até próximo scroll/troca de status —
  // por exemplo: user clica "Minhas", lista continua com convs de "Todas"
  // (cards de outros agentes aparecem sem nome de assignee, parecendo bug).
  const hadUnread = (oldVal || []).includes('unread');
  const hasUnread = (val || []).includes('unread');
  const wasMePrimary = (oldVal || [])[0] === 'me';
  const isMePrimary = (val || [])[0] === 'me';
  // SZ_FLICKER_FIX_20260522: guarda contra duplo fetch.
  // toggleChipFilter já chamou resetAndFetchData (que faz fetchConversations)
  // pra cobrir 'me'/'unread'/'mentions' fora do modo de filtros aplicados.
  // Esse watcher só precisa refetchar quando toggleChipFilter early-returnou
  // (= hasAppliedFiltersOrActiveFolders=true). chatListLoading=true significa
  // que toggleChipFilter já iniciou fetch — não duplicamos.
  if ((hadUnread !== hasUnread || wasMePrimary !== isMePrimary) && !chatListLoading.value) {
    store.dispatch('emptyAllConversations');
    if (!hasAppliedFiltersOrActiveFolders.value) {
      fetchConversations();
    } else if (hasAppliedFilters.value) {
      fetchFilteredConversations(appliedFilters.value);
    }
  }
}, { deep: true });

// SZ: persiste appliedFilters (filtros da sheet — Etiqueta, Prioridade, Período, etc.)
// quando o Vuex muda. Limpa storage quando filtros são removidos.
// Usa o getter v1 (snake_case raw) pra evitar conversão de volta na restauração.
const rawAppliedFilters = useMapGetter('getAppliedConversationFilters');
watch(rawAppliedFilters, (val) => {
  try {
    const key = szAppliedFiltersStorageKey();
    if (Array.isArray(val) && val.length > 0) {
      sessionStorage.setItem(key, JSON.stringify(val));
    } else {
      sessionStorage.removeItem(key);
    }
  } catch { /* silent */ }
}, { deep: true });

// SZ: refetch dos badges (Minhas/Não Lidas/Menções) quando filtros mudam.
// Sem isso, o usuário aplica/remove filtro e o badge fica congelado no valor
// global anterior — bug "999+ falso" mesmo com lista filtrada vazia.
// Cobre ambos: filtros customizados (Vuex) E nav URL-driven (label/team/type).
watch(appliedFilters, () => fetchSzChipCounts(), { deep: true });
watch(
  () => [props.label, props.teamId, props.conversationType, activeAssigneeTab.value],
  () => fetchSzChipCounts()
);

// SZ: detecta páginas que terminaram de carregar sem ganho visível.
// chatListLoading: true → false significa fim do fetch. Compara tamanho antes/depois.
watch(chatListLoading, (isLoading, wasLoading) => {
  if (wasLoading && !isLoading) {
    const now = conversationList.value.length;
    if (now <= lastVisibleCount.value) {
      overlayDryPages.value += 1;
    } else {
      overlayDryPages.value = 0;
    }
    lastVisibleCount.value = now;
  }
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 bg-n-solid-1 conversations-list-wrap min-w-[340px] 2xl:min-w-[412px]"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[340px] 2xl:w-[412px]',
    ]"
  >
    <slot />
    <div class="sz-chl-wrap relative">
      <ChatListHeader
        :page-title="pageTitle"
        :has-applied-filters="hasAppliedFilters"
        :has-active-folders="hasActiveFolders"
        :active-status="activeStatus"
        :is-on-expanded-layout="isOnExpandedLayout"
        :conversation-stats="conversationStats"
        :is-list-loading="chatListLoading && !conversationList.length"
        :applied-filters-count="appliedFilters.length"
        @add-folders="onClickOpenAddFoldersModal"
        @delete-folders="onClickOpenDeleteFoldersModal"
        @filters-modal="onToggleAdvanceFiltersModal"
        @reset-filters="resetAndFetchData"
        @basic-filter-change="onBasicFilterChange"
      />
      <!-- SZ: sheet de filtros — abre abaixo do header (substitui modal nativo) -->
      <SzFilterSheet
        v-model="showAdvancedFilters"
        :initial-filters="appliedFilter"
        @apply="onSzFilterApply"
        @clear="onSzFilterClear"
      />
    </div>

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <!-- SZ: banner "X conversas em [pill]" quando há filtro de etiqueta/canal/time
         ativo via sidebar. Some quando o user clica no X (volta pra Todas). -->
    <SzActiveFilterBanner
      v-if="activeFilterBannerType"
      :type="activeFilterBannerType"
      :label="activeFilterLabelObj"
      :inbox="activeFilterInboxObj"
      :team="activeFilterTeamObj"
      :count="activeFilterCount"
      @close="onCloseFilterBanner"
    />

    <!-- SZ: chips sempre visíveis — também em modo de filtros aplicados,
         onde funcionam como overlays client-side sobre o resultado filtrado. -->
    <SzAssigneeChips
      :active-filters="szActiveFilters"
      :counts="szChipCounts"
      @toggle-filter="toggleChipFilter"
    />

    <SzChatListEmpty
      v-if="!chatListLoading && !conversationList.length"
      :filter="emptyStateFilter"
      @show-mine="handleShowMine"
    />
    <ConversationBulkActions
      v-if="selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <div
      ref="conversationListRef"
      class="flex-1 overflow-hidden conversations-list hover:overflow-y-auto"
      :class="{ 'overflow-hidden': isContextMenuOpen }"
    >
      <!-- SZ: skeleton inicial quando lista vazia e ainda buscando -->
      <SzConversationCardSkeleton
        v-if="chatListLoading && !conversationList.length"
        :count="8"
      />
      <DynamicScroller
        v-else
        ref="conversationDynamicScroller"
        :items="conversationList"
        :min-item-size="24"
        class="w-full h-full overflow-auto"
      >
        <template #default="{ item, index, active }">
          <!--
            If we encounter resizing issues, we can set the `watchData` prop to true
            this will deeply watch the entire object instead of just size dependencies
            But it can impact performance
          -->
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[
              item.messages,
              item.labels,
              item.uuid,
              item.inbox_id,
            ]"
          >
            <ConversationItem
              :source="item"
              :label="label"
              :team-id="teamId"
              :folders-id="foldersId"
              :conversation-type="conversationType"
              :show-assignee="showAssigneeInConversationCard"
              @select-conversation="selectConversation"
              @de-select-conversation="deSelectConversation"
            />
          </DynamicScrollerItem>
        </template>
        <template #after>
          <!-- SZ: skeleton no fim para indicar paginação (substitui Spinner) -->
          <SzConversationCardSkeleton
            v-if="chatListLoading"
            :count="2"
          />
          <p
            v-else-if="showEndOfListMessage"
            class="p-4 text-center text-n-slate-11"
          >
            {{ $t('CHAT_LIST.EOF') }}
          </p>
          <!-- SZ_FLICKER_FIX_20260522: gate observer atrás de lista não-vazia.
               Sem isso, com lista vazia (ex: Minhas+NãoLidas sem resultados),
               o observer fica visível no viewport e dispara loadMoreConversations
               em loop, causando flicker entre skeleton e empty state. -->
          <IntersectionObserver
            v-else-if="conversationList.length > 0"
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </template>
      </DynamicScroller>
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <!-- SZ: ConversationFilter substituído pela SzFilterSheet acima -->
    <TeleportWithDirection
      v-if="showAdvancedFilters && hasActiveFolders"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
  </div>
</template>
